import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { DOCUMENT_TYPES } from '~constants/common';
import { useSearch } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, Toolbar } from '~components';
import BulkActions from '~components/BulkActions';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view-v3';
import { useDocumentBatchGeneration } from '~main/components/document-batch-jobs/useDocumentBatchGeneration';

import { columns } from '~support-at-home/forms/statements/list';
import { searchFields } from '~support-at-home/forms/statements/search';

import { getAll as getStatementDeliveryPreferences } from '~lookups/actions/statementDeliveryPreferences';
import { getAll as getStatementStatuses } from '~lookups/actions/statementStatuses';
import { getAll as getClients } from '~main/actions/clients';
import { setTitle } from '~main/actions/login';
import { useGetStatementsQuery } from '~support-at-home/actions/statements';

import StatementsWizard from './statements-wizard';

const Statements: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [params, setParams] = useSearch('statements-search', { limit: 30 });
  const search = (values: Record<string, any>) => setParams(values);

  const handleLoadMore = () => {
    setParams({ ...params, limit: (params.limit || 30) + 30 });
  };

  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [statementsSelected, setStatementsSelected] = useState<string[]>([]);

  useEffect(() => {
    if (isActive) {
      setTitle('Statements');
    }
    dispatch(getClients());
    dispatch(getStatementStatuses());
    dispatch(getStatementDeliveryPreferences());
  }, [isActive]);

  const { data, isLoading } = useGetStatementsQuery(params);
  const { generateSeparate, generateCombined, emailSeparate } = useDocumentBatchGeneration();

  const statements = data?.rows || [];
  const total = data?.total;

  // Unified bulk action handler
  const handleBulkAction = async (action: string) => {
    if (!statementsSelected.length) return;
    try {
      switch (action) {
        case 'generate-separate':
          await generateSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, statementsSelected).unwrap();
          showToast('success', `Statement generation started for ${statementsSelected.length} statement(s)`);
          break;
        case 'generate-combined':
          await generateCombined(DOCUMENT_TYPES.SAH_STATEMENT.COMBINED, statementsSelected).unwrap();
          showToast('success', `Combined statement generation started for ${statementsSelected.length} statement(s)`);
          break;
        case 'email-separate':
          await emailSeparate(DOCUMENT_TYPES.SAH_STATEMENT.SINGLE, statementsSelected).unwrap();
          showToast('success', `Statement emails scheduled for ${statementsSelected.length} statement(s)`);
          break;
        default:
          console.error('Unknown bulk action:', action);
          return;
      }
      setStatementsSelected([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Bulk action failed';
      showToast('danger', message);
      console.error('Bulk action failed:', error);
    }
  };

  const bulkActionOptions = [
    { value: 'generate-separate', label: 'Generate PDFs' },
    { value: 'generate-combined', label: 'Generate Combined PDF', minimum: 2 },
    { value: 'email-separate', label: 'Email Client Statements' },
  ];

  return (
    <div className="p-2 statements">
      <Toolbar
        id="statements"
        rightCluster={
          <>
            <BulkActions
              id="statements-bulk-actions"
              actionOptions={bulkActionOptions}
              onAction={handleBulkAction}
              selectedIds={statementsSelected}
            />
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="statements-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="statements-new-button" size="sm" color="success" onClick={() => setWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} initialValues={params} onSubmit={search} />
      <TableView
        id="statements-table"
        columns={columns}
        data={statements}
        isLoading={isLoading}
        emptyMessage="No statements found"
        allowPagination
        totalCount={total}
        showCount
        onLoadMore={handleLoadMore}
        readonly
        striped
        canSelect
        selectedIds={statementsSelected}
        onRowSelectionChange={(statementIds) =>
          setStatementsSelected((statementIds as (string | number)[]).map(String))
        }
      />
      <StatementsWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
    </div>
  );
};

export default Statements;
